import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { DealerBuyerRow } from '../../types/bruktbilfinansiering'
import DealerSoldRowsSection from './DealerSoldRowsSection'
import DealerSoldRowsUnfinancedSection from './DealerSoldRowsUnfinancedSection'
import { useDealerSoldRows } from './hooks/useDealerSoldRows'

export type SoldRowsTab = 'med' | 'uten'

interface Props {
  bankOrgNr: string
  dealerOrgNr: string
  buyerRows: DealerBuyerRow[]
  totalSales: number
  financedSales: number
  contextInfo?: string
  loanDataThrough: string
  soldRowsFilters: {
    startDate?: string
    endDate?: string
    varebil?: boolean
    classes?: string[]
    dealerPostnummer?: string
    buyerPostnummer?: string
  }
}

/** "Solgte biler" as two tabs (#407).
 *
 *  The two tabs answer the same question over different horizons: "Med
 *  finansieringsdata" carries loan columns and therefore stops at the 15-day
 *  Brreg lag; "Uten finansieringsdata" drops them and runs to the newest
 *  transfer in mv_bbf_transfers. Each states its own horizon in its header —
 *  without that, the row counts look like a bug.
 */
export default function DealerSoldRowsTabs({
  bankOrgNr, dealerOrgNr, buyerRows, totalSales, financedSales,
  contextInfo, loanDataThrough, soldRowsFilters,
}: Props) {
  const { t } = useTranslation('bbf')
  const [tab, setTab] = useState<SoldRowsTab>('med')

  const { data, loading, error } = useDealerSoldRows(
    bankOrgNr, dealerOrgNr, soldRowsFilters, tab === 'uten',
  )

  function TabButton({ value, label }: { value: SoldRowsTab; label: string }) {
    const active = tab === value
    return (
      <button
        role="tab"
        aria-selected={active}
        onClick={() => setTab(value)}
        className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
          active
            ? 'border-primary-600 text-primary-600 dark:text-primary-400'
            : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
        }`}
      >
        {label}
      </button>
    )
  }

  return (
    <div>
      <h3 className="text-lg font-semibold mb-2">{t('soldRows.title')}</h3>
      <div role="tablist" className="flex gap-1 border-b border-gray-200 dark:border-gray-700 mb-3">
        <TabButton value="med" label={t('soldRows.tabs.med')} />
        <TabButton value="uten" label={t('soldRows.tabs.uten')} />
      </div>

      {tab === 'med' ? (
        <DealerSoldRowsSection
          rows={buyerRows}
          totalSales={totalSales}
          financedSales={financedSales}
          contextInfo={contextInfo}
          hideHeading
          loanDataThrough={loanDataThrough}
        />
      ) : (
        <DealerSoldRowsUnfinancedSection
          rows={data?.rows ?? []}
          loading={loading}
          error={error}
          dataThrough={data?.data_through ?? null}
          contextInfo={contextInfo}
        />
      )}
    </div>
  )
}
